import { useRef, useState } from 'react';
import { uploadLibraryFile } from '@/hooks/useUpload';
import {
  getOrderSoundPreference,
  setOrderSoundPreference,
  playDefaultChime,
  playSoundUrl,
  type OrderSoundMode,
  type OrderSoundPreference,
} from '@/lib/orderSound';

const MAX_SOUND_BYTES = 2 * 1024 * 1024; // 2 Mo : largement suffisant pour une alerte courte

const MODES: { key: OrderSoundMode; label: string; hint: string; icon: string }[] = [
  { key: 'default', label: 'Carillon par défaut', hint: 'Un carillon léger intégré, aucun fichier requis.', icon: 'ri-notification-3-line' },
  { key: 'custom', label: 'Son personnalisé', hint: 'Importez votre propre son (MP3, WAV, OGG…).', icon: 'ri-music-2-line' },
  { key: 'none', label: 'Silencieux', hint: 'La pastille et le toast restent, mais sans son.', icon: 'ri-volume-mute-line' },
];

export default function NotificationSoundSettings() {
  const [pref, setPref] = useState<OrderSoundPreference>(() => getOrderSoundPreference());
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const savedTimer = useRef<number | null>(null);

  const flashSaved = () => {
    setSaved(true);
    if (savedTimer.current) window.clearTimeout(savedTimer.current);
    savedTimer.current = window.setTimeout(() => setSaved(false), 2500);
  };

  const apply = (next: OrderSoundPreference) => {
    setPref(next);
    setOrderSoundPreference(next);
    flashSaved();
  };

  const chooseMode = (mode: OrderSoundMode) => {
    setError('');
    // Le mode « personnalisé » garde l'éventuel fichier déjà importé.
    const next: OrderSoundPreference = { mode, url: mode === 'custom' ? pref.url : pref.url };
    apply(next);
    if (mode === 'default') playDefaultChime();
    if (mode === 'custom' && pref.url) playSoundUrl(pref.url);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    setError('');

    if (!file.type.startsWith('audio/')) {
      setError('Le fichier choisi doit être un son (MP3, WAV, OGG, M4A…).');
      return;
    }
    if (file.size > MAX_SOUND_BYTES) {
      setError('Le son doit faire moins de 2 Mo (une alerte courte suffit).');
      return;
    }

    setUploading(true);
    try {
      const safeName = (file.name || 'son.mp3').replace(/[^a-zA-Z0-9._-]/g, '_').slice(-60);
      const renamed = new File([file], safeName, { type: file.type });
      const url = await uploadLibraryFile(renamed, 'sounds');
      apply({ mode: 'custom', url });
      playSoundUrl(url);
    } catch {
      setError("Impossible d'importer le son. Réessayez avec un fichier plus léger.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
      <h3 className="text-sm font-semibold text-foreground-700 mb-1 flex items-center gap-2">
        <i className="ri-notification-3-line text-primary-600"></i>
        Alerte de nouvelle commande
      </h3>
      <p className="text-xs text-foreground-500 mb-5">
        Choisissez le son joué en direct quand une nouvelle commande arrive dans votre espace.
      </p>

      <div className="space-y-2.5">
        {MODES.map((m) => {
          const active = pref.mode === m.key;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => chooseMode(m.key)}
              className={`w-full flex items-start gap-3 p-3.5 rounded-lg border text-left transition-colors cursor-pointer ${
                active
                  ? 'border-primary-300 bg-primary-50/60'
                  : 'border-background-200/70 bg-background-50 hover:bg-background-100/60'
              }`}
            >
              <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? 'bg-primary-100 text-primary-700' : 'bg-background-100 text-foreground-500'}`}>
                <i className={m.icon}></i>
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-foreground-900">{m.label}</span>
                  {active && <i className="ri-checkbox-circle-fill text-primary-500"></i>}
                </span>
                <span className="block text-xs text-foreground-500 mt-0.5">{m.hint}</span>
              </span>
            </button>
          );
        })}
      </div>

      {pref.mode === 'custom' && (
        <div className="mt-4 p-4 rounded-lg bg-background-100/70 border border-background-200/60">
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 px-4 py-2 bg-background-50 border border-background-200/70 rounded-full text-xs font-medium text-foreground-700 whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"
            >
              {uploading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-2-line"></i>}
              {pref.url ? 'Remplacer le son' : 'Importer un son'}
            </button>
            {pref.url && (
              <button
                type="button"
                onClick={() => playSoundUrl(pref.url as string)}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-play-fill"></i>
                Écouter
              </button>
            )}
            <span className="text-[11px] text-foreground-400">
              MP3, WAV, OGG, M4A · moins de 2 Mo
            </span>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="audio/*"
            onChange={handleUpload}
            className="hidden"
          />
        </div>
      )}

      {error && (
        <p className="mt-3 text-xs text-red-600 flex items-center gap-1.5">
          <i className="ri-error-warning-line"></i>
          {error}
        </p>
      )}

      {saved && (
        <p className="mt-3 text-xs text-accent-600 flex items-center gap-1.5">
          <i className="ri-checkbox-circle-line"></i>
          Préférence enregistrée
        </p>
      )}
    </div>
  );
}